import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import { BrandSummary_T } from "../../../types/Brand";
import { getDateValue, renderDateCell, renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

interface BrandCountCountryTableProps {
  brand_id: string;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "country_name", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: ["country_name", "country_code"] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
  aggregation: {
    model: {
      total_poi_count: "sum",
      total_poi_count_filtered: "sum",
      total_poi_count_duplicates: "sum",
      total_open_poi_count: "sum",
    },
  },
};

const columns_def: GridColDef[] = [
  {
    field: "country_name",
    headerName: "Country",
    description:
      "A country where this brand has physical locations, derived from the distinct countries of places with a chain_id for this brand.",
    type: "string",
    width: 200,
  },
  {
    field: "country_code",
    headerName: "Country Code",
    description: "ISO 3166-1 alpha-2 for this country.",
    type: "string",
    width: 100,
  },
  {
    field: "total_poi_count",
    headerName: "Total POI Count",
    description:
      "The total number of places in the database associated with this brand's chain_id, excluding ignored places.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "total_poi_count_filtered",
    headerName: "Active Count",
    description:
      "The number of non-ignored, non-duplicate places associated with this brand's chain_id.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "total_poi_count_duplicates",
    headerName: "Duplicate POI Count",
    description:
      "The number of places where the parent_id field is not null, indicating known duplicates.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "total_open_poi_count",
    headerName: "Open POI Count",
    description:
      "The number of active places associated with this brand's chain_id that have a status of 'open'.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "query_last_executed",
    headerName: "Data Last Updated",
    description:
      "The date when this data was retrieved and aggregated from the places table in the production database.",
    type: "date",
    width: 200,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
];

const BrandCountCountryTable: React.FC<BrandCountCountryTableProps> = ({
  brand_id,
}) => {
  const [brandCountriesSummaryData, setBrandCountriesSummaryData] = useState<
    BrandSummary_T[]
  >([]);
  const [loading, setLoading] = useState(true);

  const { showError } = useError();
  const { apiRequest } = useApi();

  useEffect(() => {
    if (brand_id) {
      const fetchBrandCountriesSummaryData = async () => {
        try {
          const response = await apiRequest<BrandSummary_T[]>(
            `/brand_reports/brand_count_by_country/${brand_id}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setBrandCountriesSummaryData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch brand counts by country for brand ID ${brand_id}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchBrandCountriesSummaryData();
    }
  }, [brand_id]);

  return (
    <GaneshDataGrid
      loading={loading}
      data={brandCountriesSummaryData}
      columns={columns_def}
      gridId="brandCountriesSummaryGrid"
      defaultGridState={defaultGridState}
    />
  );
};

export default BrandCountCountryTable;
